import { getClientCookie, setClientCookie } from "@/lib/cookie/clientCookies";

import {
  PERSONALIZATION_FULL_PATH_COOKIE,
  PERSONALIZATION_TIME_SPENT_COOKIE,
} from "./constants";

const DEFAULT_PATH_MAP_API_URL = "https://g-api.adt.com/v1/path-map";

/**
 * Resolves the Personalization path-map API URL.
 * Defaults to production; set NEXT_PUBLIC_PERSONALIZATION_API_URL to override.
 */
export function getPersoPathMapApiUrl(): string {
  return (
    process.env.NEXT_PUBLIC_PERSONALIZATION_API_URL || DEFAULT_PATH_MAP_API_URL
  );
}

export function isPersonalizationPathMapEnabled(apiUrl: string): boolean {
  return apiUrl.length > 0;
}

function toPathMapValue(data: unknown): string | null {
  if (data == null) return null;
  if (typeof data === "string") return data || null;
  if (typeof data === "number" || typeof data === "boolean") {
    return String(data);
  }
  return null;
}

export async function fetchPathMap(
  apiUrl: string,
  url: string,
  signal?: AbortSignal,
): Promise<string | null> {
  if (!apiUrl) return null;

  const fullPath = `${apiUrl}?path=${encodeURIComponent(url)}`;
  try {
    const response = await fetch(fullPath, { signal });
    if (!response.ok) {
      throw new Error(`Failed to fetch path map: ${response.statusText}`);
    }
    if (response.status === 204) {
      return null;
    }
    return toPathMapValue(await response.json());
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      return null;
    }
    console.debug("Error fetching path map:", error);
    return null;
  }
}

export async function appendPathMap(
  apiUrl: string,
  url: string,
  signal?: AbortSignal,
): Promise<string | null> {
  if (!isPersonalizationPathMapEnabled(apiUrl)) return null;

  const pathMapValue = await fetchPathMap(apiUrl, url, signal);
  if (!pathMapValue) return null;

  const existingCookieValue =
    getClientCookie(PERSONALIZATION_FULL_PATH_COOKIE) ?? "";
  const updatedPath = existingCookieValue
    ? `${existingCookieValue}|${pathMapValue}`
    : pathMapValue;
  setClientCookie(PERSONALIZATION_FULL_PATH_COOKIE, updatedPath);
  return pathMapValue;
}

export function recordTimeSpent(
  pathMapValue: string | null,
  entryTime: number,
): void {
  if (!pathMapValue) return;

  const timeSpent = Math.floor((Date.now() - entryTime) / 1000);
  if (timeSpent < 0) return;

  const existingCookieValue =
    getClientCookie(PERSONALIZATION_TIME_SPENT_COOKIE) ?? "";
  const updatedCookie = existingCookieValue
    ? `${existingCookieValue}|${pathMapValue}:${timeSpent}`
    : `${pathMapValue}:${timeSpent}`;

  setClientCookie(PERSONALIZATION_TIME_SPENT_COOKIE, updatedCookie);
}

/** Fetch path map for a URL and append it to the persoFullPath cookie. */
export async function runPathMapForUrl(
  apiUrl: string,
  url: string,
  signal?: AbortSignal,
): Promise<string | null> {
  return appendPathMap(apiUrl, url, signal);
}
